// This file has been shamelessly copied from https://github.com/brandlive1941/events-3/pull/4645/files
// accepts an input stream of characters from the incoming fetch buffer and builds them into JSON objects
// assumes the input stream is formatted as a single JSON object per line ie: 
/*
{"type":"message","data":"hello"}
{"type":"message","data":"world"}
*/
// in this way we can progressively write rows in the API and have them be processed as they are received
function makeJsonDecoder() {
	let buf = '';
	let pos = 0;
	return new TransformStream({
		transform(chunk, controller) {
			buf += chunk;
			while (pos < buf.length) {
				if (buf[pos] == '\n') {
					const line = buf.substring(0, pos);
                    console.log('line',line);
					controller.enqueue(JSON.parse(line));
					buf = buf.substring(pos + 1);
					pos = 0;
				} else {
					++pos;
				}
			}
		}
	});
}

type InputMessage = {
	type?: string,
	data: any
}

interface StateEventMap {
	[key: string]: CustomEvent;
}

interface IFetchEventTarget extends EventTarget {
	addEventListener<K extends keyof StateEventMap>(
		type: K,
		listener: (ev: StateEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions
	): void;
	addEventListener(
		type: string,
		callback: EventListenerOrEventListenerObject | null,
		options?: EventListenerOptions | boolean
	): void;

	removeEventListener<K extends keyof StateEventMap>(
		type: K,
		listener: (ev: StateEventMap[K]) => void,
		options?: boolean | AddEventListenerOptions
	): void;
	removeEventListener(
		type: string,
		callback: EventListenerOrEventListenerObject | null,
		options?: EventListenerOptions | boolean
	): void;
}

// accepts an input stream of JSON objects and dispatches them as events on the provided event target
function makeWriteableEventStream(eventTarget: IFetchEventTarget) {
	return new WritableStream({
		start() {
			eventTarget.dispatchEvent(new Event('start'));
		},
		write(message: InputMessage | Record<string, any> | Record<string, any>[]) {
			if (!Array.isArray(message) && message.type) {
				eventTarget.dispatchEvent(new CustomEvent(message.type, { detail: message.data }));
			} else {
				eventTarget.dispatchEvent(new CustomEvent('message', { detail: message }));
			}
		},
		close() {
			eventTarget.dispatchEvent(new CustomEvent('close'));
		},
		abort(reason) {
			eventTarget.dispatchEvent(new CustomEvent('abort', { detail: reason }));
		}
	});
}

// returns an event target where we can add event listeners for incoming event rows
// should have handlers for "start", "close", "abort", and any generic message type emitted from the API side ("message" should be the generic)
export function FetchEventTarget(input: string, init?: RequestInit) {
	const eventTarget = new EventTarget() as IFetchEventTarget;

	fetch(input, init)
		.then(response => {
			const jsonDecoder = makeJsonDecoder();
			const eventStream = makeWriteableEventStream(eventTarget);

			if (!response?.body) {
				throw new Error('No response body');
			}

			response.body
				.pipeThrough(new TextDecoderStream())
				.pipeThrough(jsonDecoder)
				.pipeTo(eventStream);
		})
		.catch(error => {
			eventTarget.dispatchEvent(
				new MessageEvent('error', { data: error })
			);
		});
	return eventTarget;
}

/**
 * FetchStreamHandler is a wrapper around FetchEventTarget that provides a fluent interface for adding event listeners
 * 
 * Example usage:
 * 
 * const fetchStream = FetchStream({ url: 'https://example.com', method: 'GET', token: 'my-cool-token' });
 * 
 * fetchStream
 * 		.start(() => console.log('started'))
 * 		.on('some-generic-message', (data) => console.log(data))
 * 		.on('some-other-generic-message, (data) => console.log(data))
 * 		.subscribe((data) => console.log(data))
 * 		.catch((e) => console.error(e))
 * 		.ended(() => console.log('ended'));
 */
class FetchStreamHandler {
	private onDataHandler?: (e: CustomEvent) => void;
	private onErrorHandler?: (e: CustomEvent) => void;
	private onStartHandler?: (e: CustomEvent) => void;
	private onEndHandler?: () => void;
	private messageHandlers: Map<string, (e: CustomEvent) => void> = new Map();
	private target: IFetchEventTarget;

	constructor(target: IFetchEventTarget) {
		this.target = target;
		this.target.addEventListener('close', () => {
			this.cleanup();
			this.onEndHandler?.();
		});
	}

	on = (event: string, handler: (data: InputMessage) => void) => {
		const dataHandler = (e: CustomEvent) => handler(e.detail);
		this.messageHandlers.set(event, dataHandler);
		this.target.addEventListener(event, dataHandler);
		return this;
	};

	subscribe = (callback: (data: InputMessage) => void) => {
		if (this.onDataHandler) {
			throw new Error('Only one subscribe handler allowed - did you mean to use the .on handler instead?');
		}

		this.onDataHandler = (e: CustomEvent) => callback(e.detail);
		this.target.addEventListener('message', this.onDataHandler);
		return this;
	};

	catch = (callback: (e: CustomEvent) => void) => {
		if (this.onErrorHandler) {
			throw new Error('Only one error callback allowed');
		}

		this.onErrorHandler = (e: CustomEvent) => callback(e.detail);
		this.target.addEventListener('error', this.onErrorHandler);
		return this;
	};

	start = (callback: () => void) => {
		if (this.onStartHandler) {
			throw new Error('Only one start callback allowed');
		}

		const startHandler = () => {
			callback();
		};

		this.onStartHandler = startHandler;
		this.target.addEventListener('start', this.onStartHandler);
		return this;
	};

	cleanup = () => {
		if (this.onDataHandler) {
			this.target.removeEventListener('message', this.onDataHandler);
		}

		if (this.onErrorHandler) {
			this.target.removeEventListener('error', this.onErrorHandler);
		}

		if (this.onStartHandler) {
			this.target.removeEventListener('start', this.onStartHandler);
		}

		if (this.onEndHandler) {
			this.target.removeEventListener('close', this.onEndHandler);
		}

		for (const [key, handler] of this.messageHandlers) {
			this.target.removeEventListener(key, handler);
		}
	}

	ended = (callback: () => void) => {
		if (this.onEndHandler) {
			throw new Error('Only one ended callback allowed');
		}

		this.onEndHandler = callback;
		return this;
	};
}

export type FetchStreamInput<T = any> = {
	url: string,
	method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
	token?: string,
	data?: Record<string, T>,
}

export function FetchStream<T = any>({
	url,
	token,
	data,
	method
}: FetchStreamInput<T>) {
	const headers = new Headers();
	headers.append('Content-Type', 'application/json');

	if (token) {
		headers.append('bl-token', `${token}`);
	}

	const init: RequestInit = {
		method,
		headers,
		body: JSON.stringify(data)
	};

	const target = FetchEventTarget(url, init);

	return new FetchStreamHandler(target);
}